import { Observable, from, of } from 'rxjs';
import { defaultIfEmpty, filter, map, mergeMap, take, tap } from 'rxjs/operators';

export type PathVersionCheckResult = 'multiple' | 'single' | 'unknown';

/**
 * Decides whether any of the given paths has more than one version, which is the only
 * thing the Report Wizard needs from /paths/{guid}/versions.
 *
 * - at most `concurrency` requests in flight (never an unrestricted fan-out)
 * - counts are cached per wizard instance in `cache` (guid -> version count);
 *   failed requests (null) are never cached
 * - stops as soon as one path with >1 version is found; unsubscribing cancels in-flight
 *   requests and prevents the remaining ones from starting
 * - 'unknown' means the answer could not be established (request errors), so callers
 *   must not reset saved state
 */
export function checkMultiplePathVersions(
  guids: string[],
  cache: Map<string, number>,
  fetchCount: (guid: string) => Observable<number | null>,
  concurrency: number,
): Observable<PathVersionCheckResult> {
  const unique = [...new Set(guids)];

  // Nothing to check (e.g. empty selection): leave existing state untouched.
  if (unique.length === 0) {
    return of('unknown');
  }

  if (unique.some((g) => (cache.get(g) ?? 0) > 1)) {
    return of('multiple');
  }

  const uncached = unique.filter((g) => !cache.has(g));

  return from(uncached).pipe(
    mergeMap(
      (guid) => fetchCount(guid).pipe(map((count) => ({ guid, count }))),
      concurrency,
    ),
    tap(({ guid, count }) => {
      if (count !== null) cache.set(guid, count);
    }),
    filter(({ count }) => (count ?? 0) > 1),
    take(1),
    map((): PathVersionCheckResult => 'multiple'),
    defaultIfEmpty('single' as PathVersionCheckResult),
    map((result): PathVersionCheckResult => {
      if (result === 'multiple') return result;
      return unique.every((g) => cache.has(g)) ? 'single' : 'unknown';
    }),
  );
}
